/**
 * App.tsx — BuildPro Root component
 * Independent version WITH ContikPro ERP routes restored natively.
 */

import React, { Suspense } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { IS_TAURI } from '@/core/environment';
import { useDbReady } from '@/core/db-state';
import { Loader2, HardHat } from 'lucide-react';

const DataFolderSetup = IS_TAURI
  ? React.lazy(() => import('@/components/DataFolderSetup').then(m => ({ default: m.DataFolderSetup })))
  : null;

// ── Shared page imports ───────────────────────────────────────────────────────
import { BuildProDashboard } from '@/pages/BuildProDashboard';
import Documents from '#pages/Documents';
import Settings from '@/pages/Settings';
import InboxPage from '#pages/InboxPage';
import ImportWizard from '@/pages/ImportWizard';
import EmailTemplatesSettings from '@/pages/EmailTemplatesSettings';
import EmailTemplateEditorPage from '@/pages/EmailTemplateEditorPage';
import { ContactsPage } from '#pages/Contacts';
import { ExpensesPage } from '#pages/Expenses';
import { TemplatesPage } from '#pages/Templates';
import { ItemsPage } from '#pages/Items';

// ── BuildPro construction page imports ────────────────────────────────────────
import { ObrasPage } from '@/pages/ObrasPage';
import { SeguimientoPage } from '@/pages/SeguimientoPage';
import { EquipoPage } from '@/pages/EquipoPage';
import { ControlHorarioPage } from '@/pages/ControlHorarioPage';
import { CostesRealesPage } from '@/pages/CostesRealesPage';
import { PresupuestosIAPage } from '@/pages/PresupuestosIAPage';
import { MedicionesPage } from '@/pages/MedicionesPage';
import { ToastProvider } from '@/pages/shared/Toast';
import { ConfiguracionPage } from '@/pages/ConfiguracionPage';
import { PlanosPage } from '@/pages/PlanosPage';
import { OptimizacionVEPage } from '@/pages/OptimizacionVEPage';

// ── Vertical routes ───────────────────────────────────────────────────────────
import { extendRoutes } from '@/core/vertical-engine';

// ─── Simple error boundary ───────────────────────────────────────────────────
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: string }
> {
  state = { hasError: false, error: '' };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#010409] flex items-center justify-center p-8">
          <div className="bg-[#0d1117] rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-red-900/30">
            <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-400 text-2xl">⚠</span>
            </div>
            <h2 className="font-bold text-gray-100 text-xl mb-2">Ha ocurrido un error</h2>
            <pre className="text-red-400 text-xs bg-red-900/20 rounded-lg px-4 py-3 mb-4 font-mono text-left break-all whitespace-pre-wrap max-h-40 overflow-y-auto">
              {this.state.error}
            </pre>
            <button
              onClick={() => { this.setState({ hasError: false, error: '' }); }}
              className="px-6 py-2.5 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 transition-colors mr-2"
            >
              Reintentar
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-gray-700 text-white rounded-xl font-semibold hover:bg-gray-600 transition-colors"
            >
              Recargar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Loading screen ───────────────────────────────────────────────────────────

const AppLoadingScreen: React.FC = () => (
  <div className="min-h-screen bg-[#010409] flex items-center justify-center">
    <div className="text-center space-y-4">
      <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-700 rounded-3xl flex items-center justify-center mx-auto shadow-lg shadow-amber-500/20">
        <HardHat className="text-white" size={28} />
      </div>
      <div className="flex items-center gap-2 text-gray-400 justify-center">
        <Loader2 size={16} className="animate-spin text-amber-500" />
        <span className="text-sm font-medium">Iniciando ERP Global…</span>
      </div>
    </div>
  </div>
);

// ─── App routes ───────────────────────────────────────────────────────────────

const AppRoutes: React.FC = () => {
  const verticalRoutes = extendRoutes();

  return (
    <ToastProvider>
    <Router>
      <div className="min-h-screen bg-[#010409] text-gray-100 font-sans">
        <AppErrorBoundary>
          <Routes>
            <Route element={<Layout />}>
              {/* Core ERP pages */}
              <Route path="/" element={<BuildProDashboard />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/invoices" element={<Documents />} />
              <Route path="/quotes" element={<Documents />} />
              <Route path="/contacts" element={<ContactsPage type="client" />} />
              <Route path="/suppliers" element={<ContactsPage type="supplier" />} />
              <Route path="/expenses" element={<ExpensesPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/items" element={<ItemsPage />} />
              <Route path="/inbox" element={<InboxPage />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/email-templates" element={<EmailTemplatesSettings />} />
              <Route path="/settings/email-templates/:id" element={<EmailTemplateEditorPage />} />
              <Route path="/import" element={<ImportWizard />} />

              {/* ── Construction-specific pages ─────────────────────────────── */}
              <Route path="/obras" element={<ObrasPage />} />
              <Route path="/seguimiento" element={<SeguimientoPage />} />
              <Route path="/equipo" element={<EquipoPage />} />
              <Route path="/control-horario" element={<ControlHorarioPage />} />
              <Route path="/costes-reales" element={<CostesRealesPage />} />
              <Route path="/presupuestos-ia" element={<PresupuestosIAPage />} />
              <Route path="/mediciones" element={<MedicionesPage />} />
              <Route path="/configuracion-obra" element={<ConfiguracionPage />} />
              <Route path="/planos" element={<PlanosPage />} />
              <Route path="/optimizacion-ve" element={<OptimizacionVEPage />} />

              {/* ── Vertical routes ─────────────────── */}
              {verticalRoutes
                .filter((r: any) => r.withLayout)
                .map((r: any) => (
                  <Route key={r.path} path={r.path} element={<r.component />} />
                ))}

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>

            {verticalRoutes
              .filter((r: any) => !r.withLayout)
              .map((r: any) => (
                <Route key={r.path} path={r.path} element={<r.component />} />
              ))}
          </Routes>
        </AppErrorBoundary>
      </div>
    </Router>
    </ToastProvider>
  );
};

// ─── Root component ───────────────────────────────────────────────────────────

const App: React.FC = () => {
  const dbReady = useDbReady();

  if (IS_TAURI && !dbReady && DataFolderSetup) {
    return (
      <Suspense fallback={<AppLoadingScreen />}>
        <DataFolderSetup onReady={() => {}} />
      </Suspense>
    );
  }

  if (!dbReady) {
    return <AppLoadingScreen />;
  }

  return <AppRoutes />;
};

export default App;
